/**
 * Enhanced RxNorm Route Resolver
 * Implements algorithmic route resolution with proper RxCUI conversion,
 * RxTerms integration, and fallback mechanisms
 */

const RXNAV_BASE_URL = 'https://rxnav.nlm.nih.gov/REST';

// Dose form to route mapping table
const DOSE_FORM_TO_ROUTE_MAP = {
  // Oral forms
  'oral tablet': 'Oral',
  'oral capsule': 'Oral',
  'oral solution': 'Oral',
  'oral suspension': 'Oral',
  'oral powder': 'Oral',
  'oral film': 'Oral',
  'oral wafer': 'Oral',
  'oral lozenge': 'Oral',
  'oral disintegrating tablet': 'Oral',
  'oral delayed release tablet': 'Oral',
  'oral extended release tablet': 'Oral',
  'oral controlled release tablet': 'Oral',
  'oral sustained release tablet': 'Oral',
  'oral chewable tablet': 'Oral',
  'oral dispersible tablet': 'Oral',
  'oral effervescent tablet': 'Oral',
  'oral granule': 'Oral',
  'oral packet': 'Oral',
  'oral paste': 'Oral',
  'oral syrup': 'Oral',
  'oral elixir': 'Oral',
  
  // Injection forms
  'solution for injection': 'IM',
  'injection': 'IV',
  'injectable solution': 'IV',
  'intravenous injection': 'IV',
  'intramuscular injection': 'IM',
  'subcutaneous injection': 'SubQ',
  'intravenous solution': 'IV',
  'intramuscular solution': 'IM',
  'subcutaneous solution': 'SubQ',
  'prefilled syringe': 'SubQ',
  'auto-injector': 'SubQ',
  'cartridge': 'SubQ',
  'vial': 'IV',
  
  // Topical forms
  'topical cream': 'Topical',
  'topical gel': 'Topical',
  'topical ointment': 'Topical',
  'topical lotion': 'Topical',
  'topical foam': 'Topical',
  'topical spray': 'Topical',
  'topical solution': 'Topical',
  'topical suspension': 'Topical',
  'topical powder': 'Topical',
  'topical patch': 'Transdermal',
  'transdermal patch': 'Transdermal',
  'transdermal film': 'Transdermal',
  'transdermal system': 'Transdermal',
  
  // Other routes
  'rectal suppository': 'Rectal',
  'rectal cream': 'Rectal',
  'rectal foam': 'Rectal',
  'rectal gel': 'Rectal',
  'rectal solution': 'Rectal',
  'vaginal tablet': 'Vaginal',
  'vaginal suppository': 'Vaginal',
  'vaginal cream': 'Vaginal',
  'vaginal gel': 'Vaginal',
  'vaginal ring': 'Vaginal',
  'vaginal insert': 'Vaginal',
  'ophthalmic solution': 'Ophthalmic',
  'ophthalmic ointment': 'Ophthalmic',
  'ophthalmic gel': 'Ophthalmic',
  'ophthalmic suspension': 'Ophthalmic',
  'otic solution': 'Otic',
  'otic suspension': 'Otic',
  'otic ointment': 'Otic',
  'nasal spray': 'Intranasal',
  'nasal solution': 'Intranasal',
  'nasal gel': 'Intranasal',
  'nasal powder': 'Intranasal',
  'inhalation solution': 'Inhalation',
  'inhalation suspension': 'Inhalation',
  'inhalation powder': 'Inhalation',
  'inhalation aerosol': 'Inhalation',
  'sublingual tablet': 'Sublingual',
  'sublingual film': 'Sublingual',
  'buccal tablet': 'Buccal',
  'buccal film': 'Buccal'
};

// In-memory cache for search results and routes
const searchCache = new Map();
const routesCache = new Map();

// Debounce utilities
const debounceTimers = new Map();

/**
 * Debounce function calls
 */
function debounce(func, delay, key) {
  if (debounceTimers.has(key)) {
    clearTimeout(debounceTimers.get(key));
  }
  
  const timer = setTimeout(() => {
    func();
    debounceTimers.delete(key);
  }, delay);
  
  debounceTimers.set(key, timer);
}

/**
 * Convert medication name to RxCUI (prefer IN/PIN)
 */
async function convertNameToRxcui(medicationName) {
  const cacheKey = `rxcui_${medicationName.toLowerCase()}`;
  
  if (searchCache.has(cacheKey)) {
    return searchCache.get(cacheKey);
  }
  
  try {
    const response = await fetch(`${RXNAV_BASE_URL}/rxcui.json?name=${encodeURIComponent(medicationName)}&search=2`);
    if (!response.ok) return null;
    
    const data = await response.json();
    const idGroup = data?.idGroup;
    
    if (!idGroup || !idGroup.rxnormId || idGroup.rxnormId.length === 0) {
      searchCache.set(cacheKey, null);
      return null;
    }
    
    // Prefer IN (Ingredient) or PIN (Precise Ingredient) types
    const preferredTypes = ['IN', 'PIN'];
    const rxnormIds = idGroup.rxnormId;
    
    // First try to find IN or PIN
    for (const preferredType of preferredTypes) {
      const found = rxnormIds.find(id => id.idType === preferredType);
      if (found) {
        searchCache.set(cacheKey, found.rxcui);
        return found.rxcui;
      }
    }
    
    // Fallback to first available RxCUI
    const firstRxcui = rxnormIds[0]?.rxcui;
    searchCache.set(cacheKey, firstRxcui);
    return firstRxcui;
    
  } catch (error) {
    console.error('Error converting name to RxCUI:', error);
    searchCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Get routes from RxTerms API
 */
async function getRoutesFromRxTerms(rxcui) {
  const cacheKey = `rxterms_routes_${rxcui}`;
  
  if (routesCache.has(cacheKey)) {
    return routesCache.get(cacheKey);
  }
  
  try {
    const response = await fetch(`${RXNAV_BASE_URL}/RxTerms/rxcui/${rxcui}/allinfo.json`);
    if (!response.ok) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    const data = await response.json();
    const route = data?.rxtermsProperties?.route;
    
    if (!route) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    // Split routes by comma or semicolon and normalize
    const routes = route
      .split(/[,;]/)
      .map(r => r.trim())
      .filter(r => r.length > 0)
      .map(r => normalizeRoute(r))
      .filter(r => r);
    
    const uniqueRoutes = [...new Set(routes)].sort();
    routesCache.set(cacheKey, uniqueRoutes);
    return uniqueRoutes;
    
  } catch (error) {
    console.error('Error fetching routes from RxTerms:', error);
    routesCache.set(cacheKey, []);
    return [];
  }
}

/**
 * Normalize route names
 */
function normalizeRoute(route) {
  if (!route) return null;
  
  const normalized = route
    .toLowerCase()
    .replace(/oral pill/i, 'oral')
    .replace(/intravenous/i, 'iv')
    .replace(/subcutaneous/i, 'subq')
    .replace(/intramuscular/i, 'im')
    .replace(/injection/i, 'iv')
    .trim();
  
  // Title case the result
  return normalized
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get routes from dose form mapping (fallback)
 */
async function getRoutesFromDoseForms(rxcui) {
  const cacheKey = `doseform_routes_${rxcui}`;
  
  if (routesCache.has(cacheKey)) {
    return routesCache.get(cacheKey);
  }
  
  try {
    // Get ingredient concept first
    const ingredientResponse = await fetch(`${RXNAV_BASE_URL}/rxcui/${rxcui}/related.json?tty=IN`);
    if (!ingredientResponse.ok) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    const ingredientData = await ingredientResponse.json();
    const ingredientConcepts = ingredientData?.relatedGroup?.conceptGroup?.[0]?.concept || [];
    
    if (ingredientConcepts.length === 0) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    const ingredientRxcui = ingredientConcepts[0].rxcui;
    
    // Get SCD and SBD concepts
    const relatedResponse = await fetch(`${RXNAV_BASE_URL}/rxcui/${ingredientRxcui}/related.json?tty=SCD+SBD`);
    if (!relatedResponse.ok) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    const relatedData = await relatedResponse.json();
    const conceptGroups = relatedData?.relatedGroup?.conceptGroup || [];
    
    const allRoutes = new Set();
    
    // Sample up to 20 concepts to avoid too many API calls
    const sampleSize = 20;
    let processedCount = 0;
    
    for (const conceptGroup of conceptGroups) {
      const concepts = conceptGroup?.concept || [];
      
      for (const concept of concepts) {
        if (processedCount >= sampleSize) break;
        
        try {
          const propertyResponse = await fetch(`${RXNAV_BASE_URL}/rxcui/${concept.rxcui}/property.json?propName=DF`);
          if (propertyResponse.ok) {
            const propertyData = await propertyResponse.json();
            const doseForm = propertyData?.property?.value;
            
            if (doseForm) {
              const route = mapDoseFormToRoute(doseForm.toLowerCase());
              if (route) {
                allRoutes.add(route);
              }
            }
          }
        } catch (e) {
          // Continue with next concept
        }
        
        processedCount++;
      }
      
      if (processedCount >= sampleSize) break;
    }
    
    const routes = Array.from(allRoutes).sort();
    routesCache.set(cacheKey, routes);
    return routes;
    
  } catch (error) {
    console.error('Error fetching routes from dose forms:', error);
    routesCache.set(cacheKey, []);
    return [];
  }
}

/**
 * Map dose form to route using the mapping table
 */
function mapDoseFormToRoute(doseForm) {
  // Direct match
  if (DOSE_FORM_TO_ROUTE_MAP[doseForm]) {
    return DOSE_FORM_TO_ROUTE_MAP[doseForm];
  }
  
  // Partial match for complex dose forms
  for (const [key, route] of Object.entries(DOSE_FORM_TO_ROUTE_MAP)) {
    if (doseForm.includes(key)) {
      return route;
    }
  }
  
  return null;
}

/**
 * Main route resolution function
 */
export async function resolveMedicationRoutes(medicationName) {
  if (!medicationName || medicationName.trim().length === 0) {
    return [];
  }
  
  const cacheKey = `routes_${medicationName.toLowerCase().trim()}`;
  
  if (routesCache.has(cacheKey)) {
    return routesCache.get(cacheKey);
  }
  
  try {
    // Step 1: Convert name to RxCUI (prefer IN/PIN)
    const rxcui = await convertNameToRxcui(medicationName);
    if (!rxcui) {
      routesCache.set(cacheKey, []);
      return [];
    }
    
    // Step 2: Try RxTerms first
    let routes = await getRoutesFromRxTerms(rxcui);
    
    // Step 3: Fallback to dose form mapping if RxTerms returns nothing
    if (routes.length === 0) {
      routes = await getRoutesFromDoseForms(rxcui);
    }
    
    // Step 4: Final processing - dedupe and title case
    const finalRoutes = [...new Set(routes)]
      .map(route => route.charAt(0).toUpperCase() + route.slice(1).toLowerCase())
      .sort();
    
    routesCache.set(cacheKey, finalRoutes);
    return finalRoutes;
    
  } catch (error) {
    console.error('Error resolving medication routes:', error);
    routesCache.set(cacheKey, []);
    return [];
  }
}

/**
 * Debounced route resolution for frontend use
 */
export function debouncedResolveRoutes(medicationName, callback, delay = 300) {
  const key = `resolve_${medicationName}`;
  
  debounce(async () => {
    const routes = await resolveMedicationRoutes(medicationName);
    callback(routes);
  }, delay, key);
}

/**
 * Clear caches (useful for testing or memory management)
 */
export function clearCaches() {
  searchCache.clear();
  routesCache.clear();
  debounceTimers.forEach(timer => clearTimeout(timer));
  debounceTimers.clear();
}

/**
 * Get cache statistics
 */
export function getCacheStats() {
  return {
    searchCacheSize: searchCache.size,
    routesCacheSize: routesCache.size,
    activeDebounceTimers: debounceTimers.size
  };
}
